import {
  IFindPageQueryString,
  IHomeScreenQueryString,
  IPostsApiResponse,
  TypeHomeScreenSections,
  TypeNoviceADS,
} from "models/post";
import {
  ICountryAsset,
  ICountryAssetLite,
  ICurrency,
} from "models/Assets/ICountryAsset";
import { cvsToArray } from "helpers/textHelpers";
import { NextPageContextEnhanced } from "../../../types/page";
import { qsStringify } from "helpers/globalHelpers";
import { __fetchServerSide } from "../../../private/__fetch";
import { fetchAPIswitcher, fetchAtClientSide, TypeUserStatus} from "utils/misc/fetchClient";
import { EnumOrderMode } from "../../../enums/EnumOrderMode";
import { parseCookies } from "utils/nookies";
import { CookiesConstants } from "utils/constants";
import {CustomWindowEvents, useSetCustomWindowEvent} from "../../../hooks/useCustomWindowEvent";

export const POSTS_EXPEND = [
  "postUrl",
  "city_name",
  "neighborhood",
  "imagesCount",
  "bookmarkStatus",
  "authorisedSeller",
  "price_list",
  "custom_param_array",
  "subcategory_name",
  "mediasWith360",
  "natural_date",
  "note",
  "card",
  "category_reporting_name",
  "subcategory_reporting_name",
  "metaDescription",
  "branding",
  "is_authorized_member",
  "member_isShop",
  "city",
  "city_url",
  "contactAddon"
];

export const POST_CELL_FIELDS = [
  "id",
  "title",
  "post_url",
  "members_id",
  "price",
  "hide_user_contact_details",
  "local_phone",
  "phone",
  "masked_local_phone",
  "first_image_thumb",
  "with_video",
  "with_youtube",
  "with_360",
  "currency",
  "view_type",
  "cv_id",
  "natural_date",
  "services",
  "is_delivery_service",
  "record_expiration_date_timestamp",
  "is_verified",
  "share_deeplink"
];

export const toPostsQueryString = (
  queryString: IFindPageQueryString,
  country: ICountryAssetLite | ICountryAsset,
  currency?: ICurrency,
  context: NextPageContextEnhanced = null,
  isLoggedIn?: boolean
): string => {
  const cookies = parseCookies(context);
  let search_source = cookies[CookiesConstants.SEARCH_SOURCE];
  const isMembershipPage =
    context?.originalUrl.includes("shops") ||
    context?.originalUrl.includes("متاجر") ||
    context?.originalUrl.includes("mid");
  const qq = {
    page: Number(queryString.page) || 0,
    "per-page": queryString.PostSearch.perPage,
    ...(queryString?.withResultTags
      ? {
          withResultTags: queryString?.withResultTags,
        }
      : {}),
    ...(isMembershipPage
      ? {}
      : {
          withFacetCity: "true",
          ...(isLoggedIn ? null : { withFQA: "true" }),
          withExtraLinks: "true",
        }),
    fields: POST_CELL_FIELDS.join(","),
    expand: POSTS_EXPEND.join(","),
    PostSearch: {
      ...queryString.PostSearch,
      withPwaFacet: "true",
      withExtraLinks: "true",
      priceFrom: queryString.PostSearch.priceFrom || null,
      priceTo: queryString.PostSearch.priceTo || null,
      term: queryString.PostSearch.term
        ? encodeURI(queryString.PostSearch.term)
        : queryString?.term ? encodeURI(queryString.term) : null,
      search_type: queryString.PostSearch.search_type,
      cityId: cvsToArray(queryString.PostSearch.cityId).join(","),
      neighborhood_id: cvsToArray(queryString.PostSearch.neighborhood_id).join(
        ","
      ),
      categoryId: queryString.PostSearch.categoryId || null,
      categoriesId: queryString.PostSearch.categoriesId || null,
      subCategoryId: queryString.PostSearch.subCategoryId || null,
      lat: queryString.PostSearch.lat || null,
      lng: queryString.PostSearch.lng || null,
      orderMode:
        queryString.PostSearch.orderMode || EnumOrderMode.NEWEST,
      sort: queryString.PostSearch.sort || null,
      price_currency:
        queryString.PostSearch.price_currency ||
        country?.default_currency ||
        currency?.id,
      dynamicAttributes: queryString.PostSearch.dynamicAttributes,
      memberId: queryString.PostSearch.memberId || null,
      userName: queryString.PostSearch.userName || null,
      search_source,
    },
  };
  // delete landing-popup from query string
  delete qq.PostSearch["landing-popup"];
  let tt =
    qsStringify(qq) +
    (queryString?.filterFlags || "")
      .split(",")
      .map((k) => `&${k}=true`)
      .join("");
  //to handle yes or no options eg: "1111__yes" we should replace it with 1
  return tt?.replaceAll(/\d+__yes/g, "1").replaceAll(/\d+__no/g, "0");
};

const _cleanFacets =
  (queryString: IFindPageQueryString) =>
  (postsApiResponse: IPostsApiResponse): any => {
    if (queryString.PostSearch.dynamicAttributes) {
      let hasMultiValuesFromSameCps = false;
      for (const [_, [__, vals]] of Object.entries(
        Object.entries(queryString.PostSearch.dynamicAttributes || {})
      )) {
        hasMultiValuesFromSameCps = vals.length > 1;
        if (hasMultiValuesFromSameCps) {
          postsApiResponse._meta.pwaFacet = null;
        }
      }
    }
    delete postsApiResponse._links;

    return postsApiResponse;
  };

export const findPosts = async (
  context: NextPageContextEnhanced,
  queryString: IFindPageQueryString
): Promise<IPostsApiResponse> => {
  const isLoggedIn = !!context.getData("member_id");
  const apiQueryString = toPostsQueryString(
    queryString,
    context.countryObject,
    null,
    context,
    isLoggedIn
  );
  const url = queryString?.dlpSlug
    ? `dlp/${queryString?.dlpSlug}${apiQueryString}`
    : `posts/search-by-ids${apiQueryString}`;
  const data = await __fetchServerSide(url, context)
    .then(_cleanFacets(queryString))
    .catch((e = { response: { data: null } }) => ({
      items: [{ id: 1, title: JSON.stringify(e.response.data) }],
      _meta: { topKeywordSearches: [], totalCount: 0, extraLinks: [] },
    }));
  return data ? data : {};
};

export const findPostsClientSide = async (
  queryString: IFindPageQueryString,
  country?: ICountryAssetLite,
  currency?: ICurrency,
  abortController?: any,
  userStatus: TypeUserStatus = TypeUserStatus.ANONYMOUS
): Promise<IPostsApiResponse> => {
  const apiQueryString = toPostsQueryString(queryString, country, currency);
  const url = queryString?.dlpSlug
    ? `dlp/${queryString?.dlpSlug}${apiQueryString}`
    : `posts/search-by-ids${apiQueryString}`;
  const data = await fetchAPIswitcher(userStatus)({
    url,
    abortController,
  }).then(_cleanFacets(queryString));
  useSetCustomWindowEvent(CustomWindowEvents.onSERPFilterChange, {
    detail: { share_deeplink: data._meta.share_deeplink },
  });
  return data as IPostsApiResponse;
};

export const getHomeScreenDataClientSide = async (
  section: TypeHomeScreenSections,
  queryString: IHomeScreenQueryString,
  abortController?: any
): Promise<IPostsApiResponse> => {
  queryString[section]["expand"] =
    "price_list,category_reporting_name,subcategory_reporting_name";
  queryString[section]["fields"] =
    "first_image_thumb,first_image_uri,title,price,currency,id,postUrl,post_url";
  if (queryString[section]["cityId"]) {
    queryString[section]["cityId"] = queryString[section]["cityId"]
      .split(",")
      .filter((it) => it !== "undefined")
      .filter(Boolean)
      .join(",");
  }
  if (queryString?.[section]?.["neighborhood_id"]) {
    queryString[section]["neighborhood_id"] = queryString[section][
      "neighborhood_id"
    ]
      .split(",")
      .filter((it) => it !== "undefined")
      .filter(Boolean)
      .join(",");
  }
  const apiQueryString = qsStringify({
    ...queryString,
  }).replaceAll("dynamicAttributes", "dynamicAttributesIds");
  let url = `home-screen${apiQueryString}`;
  const data = await fetchAtClientSide<TypeNoviceADS>({
    url,
    abortController,
  });
  return data?.[section];
};

export const getRecommendedPostsClientSide = async (
  queryString: IFindPageQueryString,
  abortController?: any
): Promise<IPostsApiResponse> => {
  const url = `posts/recommendation-by-ids${toPostsQueryString(
    queryString,
    null
  )}`;

  const data = await fetchAtClientSide<IPostsApiResponse>({
    url,
    abortController,
  }).catch((e = { response: { data: null } }) => ({
    items: [],
    _meta: { topKeywordSearches: [], totalCount: 0, extraLinks: [] },
  }));
  return data as IPostsApiResponse;
};

export const getRecommendedPostsServerSide = async (
  context: NextPageContextEnhanced,
  queryString: IFindPageQueryString
): Promise<IPostsApiResponse> => {
  const url = `posts/recommendation-by-ids${toPostsQueryString(
    queryString,
    null
  )}`;

  return __fetchServerSide(url, context);
};
